import { MAINTENANCE_SLUG, TEST_RIDE_SLUG } from "@/lib/booking";
import type { AiPageSurface, AiProvider } from "@/lib/ai_actions";

type AnalyticsParams = Record<string, string | number | boolean | undefined>;

export function trackEvent(name: string, params: AnalyticsParams = {}) {
  if (typeof gtag !== "function") return;

  try {
    gtag("event", name, params);
  } catch {
    // Analytics is best-effort and must never block navigation or booking.
  }
}

interface LinkEvent {
  name: string;
  params: AnalyticsParams;
}

function eventForLink(anchor: HTMLAnchorElement): LinkEvent | null {
  let url: URL;
  try {
    url = new URL(anchor.href, window.location.href);
  } catch {
    return null;
  }

  const page_path = window.location.pathname;

  if (url.protocol === "tel:") {
    return { name: "phone_click", params: { page_path } };
  }

  if (url.protocol === "sms:") {
    return { name: "sms_click", params: { page_path } };
  }

  if (url.protocol === "mailto:") {
    return { name: "email_click", params: { page_path } };
  }

  if (/(^|\.)google\.com$/.test(url.hostname) && url.pathname.startsWith("/maps/dir")) {
    return { name: "directions_click", params: { link_url: anchor.href, page_path } };
  }

  if (/(^|\.)google\.com$/.test(url.hostname) && url.pathname.startsWith("/maps/search")) {
    return { name: "reviews_click", params: { link_url: anchor.href, page_path } };
  }

  if (url.origin === window.location.origin && url.pathname === "/book") {
    return { name: "test_ride_click", params: { link_url: anchor.href, page_path } };
  }

  if (/(^|\.)cal\.com$/.test(url.hostname) && url.pathname.endsWith(`/${MAINTENANCE_SLUG}`)) {
    return { name: "service_booking_click", params: { link_url: anchor.href, page_path } };
  }

  return null;
}

export function installConversionTracking() {
  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const anchor = target.closest<HTMLAnchorElement>("a[href]");
    if (!anchor) return;

    const linkEvent = eventForLink(anchor);
    if (linkEvent) trackEvent(linkEvent.name, linkEvent.params);
  });
}

export function trackBookingCreated(slug: string, data: AnalyticsParams = {}) {
  const eventName = slug === MAINTENANCE_SLUG
    ? "service_booking_created"
    : "test_ride_booking_created";

  trackEvent(eventName, {
    booking_type: slug === MAINTENANCE_SLUG ? "service" : "test_ride",
    event_slug: slug || TEST_RIDE_SLUG,
    ...data,
  });
}

/**
 * AI-link analytics deliberately accept only routing metadata. Prompt text and
 * family details never enter analytics.
 */
export function trackAiAsk(provider: AiProvider, pageSurface: AiPageSurface, bikeHandle?: string) {
  trackEvent("ai_assistant_click", {
    provider,
    page_surface: pageSurface,
    bike_handle: bikeHandle,
  });
}

/**
 * Bike-detail analytics deliberately accept only routing metadata. The handle
 * and brand slug identify the page; contact details never enter analytics.
 */
export function trackBikeView(bikeHandle: string, brand: string) {
  trackEvent("bike_view", { bike_handle: bikeHandle, brand, page_path: window.location.pathname });
}

const BOOKING_FUNNEL_EVENTS = {
  viewed: "booking_viewed",
  slot_selected: "booking_slot_selected",
  waiver_started: "booking_waiver_started",
  waiver_completed: "booking_waiver_completed",
  confirmed: "booking_confirmed",
  failed: "booking_failed",
} as const;

type BookingFunnelStage = keyof typeof BOOKING_FUNNEL_EVENTS;

/**
 * Headless-booking analytics are deliberately unable to accept arbitrary
 * fields. A stage and a safe error code are the whole contract: no contact,
 * rider, appointment, signature, waiver, IP, or note data can be attached.
 */
export function trackBookingFunnel(stage: BookingFunnelStage, errorCode?: string) {
  const params: AnalyticsParams = { flow: "headless_test_ride" };
  if (stage === "failed" && errorCode) params.error_code = errorCode.slice(0, 80);
  trackEvent(BOOKING_FUNNEL_EVENTS[stage], params);
}
